import { CartAddEvent } from '@theme/events';

/**
 * Some product pages on this site are built with the "Instant Section &
 * Page Builder" app. That app's own "Add to Cart" buttons are plain <a>
 * tags pointing at Shopify's classic cart permalink URLs
 * (e.g. /cart/52546253652268:1?storefront=true). Left alone, clicking one
 * of these adds the item to the cart AND performs a full-page navigation to
 * /cart as a side effect of Shopify's native permalink handling - which
 * completely bypasses the theme's AJAX add-to-cart flow and the custom cart
 * drawer, dumping the shopper on the plain native cart page instead.
 *
 * This script intercepts clicks on permalink-style links anywhere in the
 * document, adds the line(s) via the Ajax Cart API instead, and dispatches
 * a CartAddEvent so the existing cart-drawer / cart-items-component wiring
 * opens the drawer and renders the new contents - exactly as if the click
 * had gone through the theme's native product-form flow.
 */

const CART_PERMALINK_PATTERN = /^\/cart\/(\d+:\d+(?:,\d+:\d+)*)\/?$/;

/**
 * Parses a cart permalink path into its variant/quantity pairs.
 * @param {string} pathname
 * @returns {{ id: string, quantity: number }[] | null}
 */
function parseCartPermalink(pathname) {
  const match = pathname.match(CART_PERMALINK_PATTERN);
  if (!match) return null;

  return match[1].split(',').map((pair) => {
    const [id, quantity] = pair.split(':');
    return { id, quantity: parseInt(quantity, 10) || 1 };
  });
}

/**
 * Builds the section ids that should be refreshed after a cart mutation,
 * matching the pattern used by the theme's own product-form component.
 * @returns {string[]}
 */
function getCartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      ids.add(item.dataset.sectionId);
    }
  });
  return Array.from(ids);
}

/**
 * Adds a single line to the cart via the Ajax Cart API.
 *
 * IMPORTANT: this store's checkout validation only accepts
 * `application/x-www-form-urlencoded` bodies for /cart/add.js - a JSON
 * body can be rejected for variants tied to a selling plan even when the
 * same fields are present. Always add lines this way for consistency with
 * the rest of the custom cart code.
 *
 * @param {{ id: string, quantity: number }} line
 * @returns {Promise<Object>} the parsed JSON response
 */
function addLineToCart({ id, quantity }) {
  const params = new URLSearchParams();
  params.append('id', id);
  params.append('quantity', String(quantity));
  const sectionIds = getCartSectionIds();
  if (sectionIds.length) params.append('sections', sectionIds.join(','));

  return fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: params.toString(),
  }).then((response) => response.json());
}

/**
 * Adds each line in sequence via the Ajax Cart API (a permalink can carry
 * more than one variant:quantity pair), returning the most recent response.
 * @param {{ id: string, quantity: number }[]} lines
 * @returns {Promise<Object|null>}
 */
async function addLinesToCart(lines) {
  let lastResponse = null;
  for (const line of lines) {
    lastResponse = await addLineToCart(line);
    if (lastResponse && lastResponse.status) {
      throw new Error(
        `Flynnys permalink intercept: add to cart failed - ${lastResponse.description || lastResponse.message}`
      );
    }
  }
  return lastResponse;
}

/** @param {MouseEvent} event */
function handleDocumentClick(event) {
  if (event.defaultPrevented) return;
  if (event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
  if (!(anchor instanceof HTMLAnchorElement)) return;
  if (anchor.target && anchor.target !== '_self') return;

  let url;
  try {
    url = new URL(anchor.href, window.location.href);
  } catch (error) {
    return;
  }

  if (url.origin !== window.location.origin) return;

  const lines = parseCartPermalink(url.pathname);
  if (!lines || !lines.length) return;

  // Intercept during the CAPTURE phase (see the listener registration below):
  // this store's "Instant Section & Page Builder" app attaches its own click
  // handler directly to these anchors that navigates the browser as soon as
  // the click reaches the element itself - before a bubble-phase listener on
  // `document` would ever run. Registering our listener on `document` with
  // `capture: true` guarantees it runs first (capture always travels from
  // the document down to the target), so preventDefault()/stopPropagation()
  // here actually stop the click before that handler fires.
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  if (anchor.hasAttribute('data-flynnys-busy')) return;
  anchor.setAttribute('data-flynnys-busy', '');
  anchor.classList.add('fc-permalink-loading');

  addLinesToCart(lines)
    .then((response) => {
      document.dispatchEvent(
        new CartAddEvent({}, lines[0].id, {
          source: 'flynnys-permalink-intercept',
          variantId: lines[0].id,
          sections: response ? response.sections : undefined,
        })
      );
    })
    .catch((error) => {
      console.error(error);
      // Fall back to the native permalink behaviour so the shopper still
      // reaches their cart rather than the click silently doing nothing.
      window.location.href = anchor.href;
    })
    .finally(() => {
      anchor.removeAttribute('data-flynnys-busy');
      anchor.classList.remove('fc-permalink-loading');
    });
}

document.addEventListener('click', handleDocumentClick, true);
